import React, {useCallback, useEffect, useMemo, useState} from "react";
import {DialogActions, DialogContent, DialogTitle, Divider} from "@mui/material";
import {Row} from "reactstrap";
import Button from "../../base/button/button";
import Dialog from "../../base/dialog";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import ApiResponseCodes from "../../../../core/models/static/api-response-codes";
import {Switch} from "../../base/switch";
import FreeShippingFeesManagementViewSettingsGeneralExceptions
    from "../../../views/panel/common/shipping-fees/free-shipping-fees-management/sections/settings/sections/general";
import FreeShippingFeesManagementViewSettingsProductsExceptions
    from "../../../views/panel/common/shipping-fees/free-shipping-fees-management/sections/settings/sections/products";
import FreeShippingFeesManagementViewSettingsCategoriesExceptions
    from "../../../views/panel/common/shipping-fees/free-shipping-fees-management/sections/settings/sections/categories";
import FreeShippingFeesManagementViewSettingsPriceLevelsExceptions
    from "../../../views/panel/common/shipping-fees/free-shipping-fees-management/sections/settings/sections/price-levels";
import FreeShippingFeesManagementViewSettingsCompaniesExceptions
    from "../../../views/panel/common/shipping-fees/free-shipping-fees-management/sections/settings/sections/companies";
import FreeShippingFeesManagementViewSettingsRegionsExceptions
    from "../../../views/panel/common/shipping-fees/free-shipping-fees-management/sections/settings/sections/regions";

const FreeFreightEligibilityFullDetailsDialog = ({open, close, data, customerId, customerName}) => {
    const [customerDetached, setCustomerDetached] = useState(false);
    const [syncWithDetachedCustomers, setSyncWithDetachedCustomers] = useState(false);
    const isMounted = useIsMounted();

    const appliedCustomerId = useMemo(() => {
        if (customerDetached) {
            return customerId;
        }
        return null;
    }, [customerDetached, customerId])

    /**
     * Fetches the customer's detached status.
     * @type {(function(): Promise<undefined|void>)|*}
     */
    const getCustomerDetachedStatus = useCallback(async () => {
        const response = await Api.getFreightCustomerExceptionInfo(customerId);
        if (!isMounted())
            return;
        if (response.errorCode === ApiResponseCodes.freeFreightCustomerNotDetached) {
            return setCustomerDetached(false);
        } else {
            setCustomerDetached(true);
        }
    }, [customerId, isMounted])

    /**
     * With every dialog open, we fetch the customer's detached status.
     * - If the customer is detached, we set the customerDetached state to true.
     * - If the customer is not detached, we set the customerDetached state to false.
     * - If the customer is not detached, we set the syncWithDetachedCustomers state to false.
     */
    useEffect(() => {
        if (!open) {
            setCustomerDetached(false);
            setSyncWithDetachedCustomers(false);
            return;
        }
        void getCustomerDetachedStatus();
    }, [open, getCustomerDetachedStatus])

    return (
        <>
            <Dialog
                open={open}
                setOpen={close}
                maxWidth={'md'}
                className={'bound-dialog free-freight-eligibility-full-details-dialog'}>
                <DialogTitle>
                    <Row xs={12} className='no-gutters justify-content-center p-3 w-100'>
                        <p className={`text-lg text-color-1 my-2 font-weight-semi-bold text-center`}>
                            Free Freight Ineligibility Details
                        </p>
                    </Row>
                    <Divider className='divider'/>
                </DialogTitle>
                <DialogContent className={'p-4'}>
                    <div className={'mb-5'}>
                        {
                            customerDetached
                                ? <p className={'text-sm'}>
                                    {customerName} is <span className={'font-weight-bold'}>detached</span> from the free freight's system
                                    rules. Any changes made will only affect this
                                    customer.
                                </p>
                                : <div className={'d-flex w-100 align-items-start justify-content-between'}>
                                    <p className={'text-sm'}>
                                        {customerName} follows the free freight's system rules. You can apply all the changes to the
                                        detached customers as well.
                                    </p>
                                    <Switch
                                        color={'primary'}
                                        checked={syncWithDetachedCustomers ?? false}
                                        onChange={setSyncWithDetachedCustomers}
                                    />
                                </div>
                        }
                    </div>
                    <div className={'section'}>
                        <div className={'section-paper p-0 mb-3'}>
                            <p className={'section-title'}>
                                Ineligibility Reasons
                            </p>
                            <ul className={'reasons-list'}>
                                {
                                    data?.friendlyReason
                                        ?.split('\n')
                                        ?.filter(e => !!e)
                                        ?.map((messsage, i) => (
                                            <li key={i}>
                                                {messsage}
                                            </li>
                                        ))
                                }
                            </ul>
                        </div>
                    </div>
                    <Divider className={'divider'}/>
                    {
                        !!data?.flatExceptions?.length &&
                        <>
                            <FreeShippingFeesManagementViewSettingsGeneralExceptions
                                data={data?.flatExceptions}
                                syncWithDetachedCustomers={syncWithDetachedCustomers}
                                customerId={appliedCustomerId}
                                customerName={customerName}
                            />
                            <Divider className={'divider'}/>
                        </>
                    }
                    {
                        !!data?.partExceptions?.length &&
                        <>
                            <FreeShippingFeesManagementViewSettingsProductsExceptions
                                data={data?.partExceptions}
                                syncWithDetachedCustomers={syncWithDetachedCustomers}
                                customerId={appliedCustomerId}
                                customerName={customerName}
                            />
                            <Divider className={'divider'}/>
                        </>
                    }
                    {
                        !!data?.partCategoryExceptions?.length &&
                        <>
                            <FreeShippingFeesManagementViewSettingsCategoriesExceptions
                                data={data?.partCategoryExceptions}
                                syncWithDetachedCustomers={syncWithDetachedCustomers}
                                customerId={appliedCustomerId}
                                customerName={customerName}
                            />
                            <Divider className={'divider'}/>
                        </>
                    }
                    {
                        !!data?.priceLevelExceptions?.length &&
                        <>
                            <FreeShippingFeesManagementViewSettingsPriceLevelsExceptions
                                data={data?.priceLevelExceptions}
                                syncWithDetachedCustomers={syncWithDetachedCustomers}
                                customerId={appliedCustomerId}
                                customerName={customerName}
                            />
                            <Divider className={'divider'}/>
                        </>
                    }
                    {
                        !!data?.freightCompanyExceptions?.length &&
                        <>
                            <FreeShippingFeesManagementViewSettingsCompaniesExceptions
                                data={data?.freightCompanyExceptions}
                                syncWithDetachedCustomers={syncWithDetachedCustomers}
                                customerId={appliedCustomerId}
                                customerName={customerName}
                            />
                            <Divider className={'divider'}/>
                        </>
                    }
                    {
                        !!data?.regionExceptions?.length &&
                        <>
                            <FreeShippingFeesManagementViewSettingsRegionsExceptions
                                data={data?.regionExceptions}
                                syncWithDetachedCustomers={syncWithDetachedCustomers}
                                customerId={appliedCustomerId}
                                customerName={customerName}
                            />
                            <Divider className={'divider'}/>
                        </>
                    }
                </DialogContent>
                <DialogActions>
                    <Row className={'w-100 no-gutters'}>
                        <Divider className='divider'/>
                        <Row className='no-gutters justify-content-between align-items-start w-100'>
                            <Button
                                disabled={!open}
                                variant={'dialog'}
                                onClick={() => close()}
                                borderRadius={'0 8px 0 8px'}
                            >
                                Close
                            </Button>
                        </Row>
                    </Row>
                </DialogActions>
            </Dialog>
        </>
    )
}

export default FreeFreightEligibilityFullDetailsDialog;
